import { useMemo } from 'react';
import { useStore } from '@nanostores/react';
import useIsHydrated from '@klaviyo-mkt/components/hooks/useIsHydrated';

import { $loggedIn, $isPartner } from '@academy/skilljar/stores/user';
import { $skilljarUserSignupFields } from '@academy/skilljar/stores/user-signup-fields';

/**
 * Custom hook to generate the partner profile URL for Academy users
 * Returns a custom Salesforce portal URL for partners, or undefined for regular users
 */
export const usePartnerProfileUrl = (): string | undefined => {
  const hasClientRendered = useIsHydrated();
  const loggedIn = useStore($loggedIn);
  const isPartner = useStore($isPartner);
  const signupFields = useStore($skilljarUserSignupFields);

  // Generate the partner profile URL if user is a partner and has salesforce_account_id
  const academyProfileHref = useMemo(() => {
    if (!hasClientRendered || !loggedIn || !isPartner) {
      return undefined;
    }

    const salesforceAccountId = signupFields?.salesforce_account_id?.value;
    const siteUrl = signupFields?.siteurl?.value;

    if (salesforceAccountId && siteUrl) {
      // Construct the partner URL: siteUrl/s/account/{Account ID}/
      // The Account Name will be auto-added when the URL redirects
      return `${siteUrl}/s/account/${salesforceAccountId}/`;
    }

    // Fall back to default Academy profile URL (will be handled by translation in component)
    return undefined;
  }, [hasClientRendered, loggedIn, isPartner, signupFields]);

  return academyProfileHref;
};